import { RenderTexture, createColorTexture, createFramebuffer } from './RenderTarget';

/**
 * Ping-pong texture + FBO pair for temporal accumulation passes (TAA, future
 * temporal SSAO/volumetric/bloom). Each frame: write to `current()`, read from
 * `previous()`, then `swap()` so this frame becomes next frame's history.
 *
 * Defaults to RGBA16F (HDR) so post-processing precision isn't lost before
 * tone mapping; consumers can override the format for cheaper temporal stores.
 */
export class HistoryBuffer {
  private textures: [RenderTexture, RenderTexture];
  private fbos: [WebGLFramebuffer, WebGLFramebuffer];
  private currentIdx = 0;
  private width: number;
  private height: number;
  private internalFormat: number;
  private format: number;
  private type: number;
  private filter: number;

  constructor(
    gl: WebGL2RenderingContext,
    width: number,
    height: number,
    internalFormat: number = gl.RGBA16F,
    format: number = gl.RGBA,
    type: number = gl.HALF_FLOAT,
    filter: number = gl.LINEAR,
  ) {
    this.width = width;
    this.height = height;
    this.internalFormat = internalFormat;
    this.format = format;
    this.type = type;
    this.filter = filter;
    this.textures = [this.createTexture(gl), this.createTexture(gl)];
    this.fbos = [
      createFramebuffer(gl, this.textures[0].glTexture!),
      createFramebuffer(gl, this.textures[1].glTexture!),
    ];
  }

  private createTexture(gl: WebGL2RenderingContext): RenderTexture {
    const tex = new RenderTexture();
    tex.glTexture = createColorTexture(
      gl,
      this.width,
      this.height,
      this.internalFormat,
      this.format,
      this.type,
      this.filter,
    );
    return tex;
  }

  /** Texture to write into this frame. */
  current(): RenderTexture {
    return this.textures[this.currentIdx];
  }

  /** Last frame's result — read source for temporal blending. */
  previous(): RenderTexture {
    return this.textures[1 - this.currentIdx];
  }

  /** FBO bound to `current()`. */
  currentFBO(): WebGLFramebuffer {
    return this.fbos[this.currentIdx];
  }

  /** Call once per frame after writing. The next frame's "previous" becomes this frame's write. */
  swap(): void {
    this.currentIdx = 1 - this.currentIdx;
  }

  resize(gl: WebGL2RenderingContext, width: number, height: number): void {
    this.destroy(gl);
    this.width = width;
    this.height = height;
    this.textures = [this.createTexture(gl), this.createTexture(gl)];
    this.fbos = [
      createFramebuffer(gl, this.textures[0].glTexture!),
      createFramebuffer(gl, this.textures[1].glTexture!),
    ];
    this.currentIdx = 0;
  }

  destroy(gl: WebGL2RenderingContext): void {
    gl.deleteFramebuffer(this.fbos[0]);
    gl.deleteFramebuffer(this.fbos[1]);
    if (this.textures[0].glTexture) gl.deleteTexture(this.textures[0].glTexture);
    if (this.textures[1].glTexture) gl.deleteTexture(this.textures[1].glTexture);
  }
}
